'use client';

import type { KeyboardEvent as ReactKeyboardEvent, TextareaHTMLAttributes } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { useTipTapEditor } from '@/hooks/annotations/useTipTapEditor';
import { AISuggestionBubble } from './AISuggestionBubble';
import { unescapeAllLatex } from './latex-unescape';
import {
  createEditableExtensions,
  createEditorAttributes,
  createTipTapWrapper,
  sharedParseOptions,
} from './tiptap-config';
import { useTipTapEffects } from './use-tiptap-effects';

export type TipTapTextEditorProps = {
  value: string;
  onValueChange?: (newValue: string) => void;
  className?: string;
  onKeyDown?: (event: ReactKeyboardEvent<HTMLTextAreaElement>) => void;
  token?: string;
} & TextareaHTMLAttributes<HTMLTextAreaElement>;

export type TipTapTextEditorRef = {
  getMarkdownOutput: () => string;
};

export const TipTapTextEditor = forwardRef<TipTapTextEditorRef, TipTapTextEditorProps>(({
  value,
  onValueChange,
  className,
  onKeyDown: externalOnKeyDown,
  token,
  placeholder,
}, ref) => {
  const lastValueRef = useRef(value);
  const { setEditor } = useTipTapEditor();
  const wrapperClasses = createTipTapWrapper(className);

  const editor = useEditor({
    extensions: createEditableExtensions(token, placeholder),
    content: value,
    onUpdate: ({ editor }) => {
      const rawMarkdown = editor.storage.markdown.getMarkdown();

      // Use LaTeX unescape function
      const processedMarkdown = unescapeAllLatex(rawMarkdown);

      if (processedMarkdown !== lastValueRef.current) {
        lastValueRef.current = processedMarkdown;
        onValueChange?.(processedMarkdown);
      }
    },

    editorProps: {
      attributes: createEditorAttributes(),
    },
    enableInputRules: true,
    enablePasteRules: true,
    editable: true,
    parseOptions: sharedParseOptions,
    immediatelyRender: false,
  });

  // Use shared effects hook
  useTipTapEffects(editor, value, setEditor, { checkFocus: true });

  // Expose getMarkdownOutput through ref
  useImperativeHandle(ref, () => ({
    getMarkdownOutput: () => {
      if (editor) {
        const rawMarkdown = editor.storage.markdown.getMarkdown();
        return unescapeAllLatex(rawMarkdown);
      }
      return '';
    },
  }), [editor]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((event: globalThis.KeyboardEvent) => {
    // Submit form on Meta+Enter
    if (event.key === 'Enter' && event.metaKey && !event.shiftKey && !event.ctrlKey && !event.altKey) {
      event.preventDefault();
      const form = editor?.view.dom.closest('form');
      if (form) {
        form.requestSubmit();
      }
      return;
    }
    externalOnKeyDown?.(event as unknown as ReactKeyboardEvent<HTMLTextAreaElement>);
  }, [editor, externalOnKeyDown]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    const view = editor.view;
    view.dom.addEventListener('keydown', handleKeyDown);
    return () => {
      view.dom.removeEventListener('keydown', handleKeyDown);
    };
  }, [editor, handleKeyDown]);

  if (!editor) {
    return null;
  }

  return (
    <div className={`${wrapperClasses.wrapper} group`}>
      {/* AI Suggestions Menu */}
      {editor && <AISuggestionBubble editor={editor} />}

      <div className={wrapperClasses.container}>
        <div className={wrapperClasses.content}>
          <EditorContent editor={editor} />
        </div>
      </div>
    </div>
  );
});
